import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Spinner } from '../common/Spinner.js'
import {
  YOUTUBE_TITLE_MARKERS,
  insertYoutubeTitleMarker,
  youtubeTitleTemplateError,
} from '../../lib/youtubeTitleTemplate.js'
import {
  youtubeApi,
  type YoutubeConfig,
  type YoutubeConfigPatch,
  type YoutubeDeviceCode,
  type YoutubePreview,
} from '../../lib/settingsApi.js'

/**
 * Titre et description du direct YouTube permanent, tenus à jour depuis la
 * grille par le serveur (`youtube-sync.service.ts`).
 *
 * L'écran sert trois usages :
 *
 * 1. **Brancher la chaîne**, une fois : identifiants OAuth du projet Google,
 *    puis autorisation par code (`google.com/device`), sans URL de retour.
 * 2. **Régler ce qui est publié** : vidéo du direct, modèle de titre, titre
 *    hors programme, fin de description.
 * 3. **Vérifier avant d'activer** : l'aperçu montre exactement ce qui
 *    partirait maintenant, sans rien envoyer.
 *
 * Le secret OAuth est en écriture seule : un champ vide conserve celui
 * enregistré.
 */

type Busy = 'save' | 'preview' | 'sync' | 'authorize' | 'disconnect' | 'footer' | null
type Message = { tone: 'ok' | 'error'; text: string } | null

const PAUSE_LABELS: Record<string, string> = {
  QUOTA: 'Quota YouTube du jour épuisé',
  RETRY: 'Nouvelle tentative programmée',
  AUTH:  'Autorisation Google à renouveler',
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function formatStamp(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' }) : '—'
}

export const YoutubeSettings = memo(function YoutubeSettings() {
  const [config, setConfig]     = useState<YoutubeConfig | null>(null)
  const [secret, setSecret]     = useState('')
  const [videoInput, setVideoInput] = useState('')
  const [dirty, setDirty]       = useState(false)
  const [busy, setBusy]         = useState<Busy>(null)
  const [message, setMessage]   = useState<Message>(null)
  const [preview, setPreview]   = useState<YoutubePreview | null>(null)
  const [device, setDevice]     = useState<YoutubeDeviceCode | null>(null)
  const pollTimer = useRef<number | null>(null)
  const templateInput = useRef<HTMLInputElement | null>(null)

  const fail = useCallback((error: unknown) => setMessage({ tone: 'error', text: errorText(error) }), [])

  const applyConfig = useCallback((next: YoutubeConfig) => {
    setConfig(next)
    setVideoInput(next.videoId ?? '')
    setDirty(false)
  }, [])

  const loadPreview = useCallback(async () => {
    try {
      setPreview(await youtubeApi.preview())
    } catch { setPreview(null) }
  }, [])

  useEffect(() => {
    void youtubeApi.get()
      .then(({ config: loaded }) => { applyConfig(loaded); return loadPreview() })
      .catch(fail)
  }, [applyConfig, loadPreview, fail])

  const stopPolling = useCallback(() => {
    if (pollTimer.current !== null) window.clearTimeout(pollTimer.current)
    pollTimer.current = null
  }, [])

  useEffect(() => stopPolling, [stopPolling])

  const patch = useCallback((values: Partial<YoutubeConfig>) => {
    setConfig(current => current ? { ...current, ...values } : current)
    setDirty(true)
  }, [])

  const insertMarker = useCallback((token: string) => {
    const input = templateInput.current
    if (!config || !input) return
    const { value, caret } = insertYoutubeTitleMarker(
      config.titleTemplate,
      token,
      input.selectionStart ?? config.titleTemplate.length,
      input.selectionEnd ?? config.titleTemplate.length,
    )
    patch({ titleTemplate: value })
    // Le curseur revient après le marqueur, pour enchaîner la saisie.
    requestAnimationFrame(() => { input.focus(); input.setSelectionRange(caret, caret) })
  }, [config, patch])

  const templateError = config ? youtubeTitleTemplateError(config.titleTemplate) : null

  const handleSave = useCallback(async () => {
    if (!config) return
    setBusy('save'); setMessage(null)
    try {
      const body: YoutubeConfigPatch = {
        enabled:           config.enabled,
        videoId:           videoInput.trim() === '' ? null : videoInput.trim(),
        titleTemplate:     config.titleTemplate,
        idleTitle:         config.idleTitle,
        descriptionFooter: config.descriptionFooter,
        titleLeadSec:      config.titleLeadSec,
        oauthClientId:     config.oauthClientId?.trim() || null,
      }
      // Champ vide = secret inchangé : sinon chaque enregistrement d'un autre
      // réglage effacerait l'identifiant Google.
      if (secret !== '') body.oauthClientSecret = secret
      const { config: saved } = await youtubeApi.update(body)
      applyConfig(saved)
      setSecret('')
      setMessage({ tone: 'ok', text: 'Configuration enregistrée' })
      await loadPreview()
    } catch (error) { fail(error) } finally { setBusy(null) }
  }, [config, videoInput, secret, applyConfig, loadPreview, fail])

  const poll = useCallback((intervalSec: number) => {
    stopPolling()
    pollTimer.current = window.setTimeout(async () => {
      try {
        const result = await youtubeApi.pollAuthorization()
        if (result.status === 'PENDING') { poll(intervalSec); return }
        setDevice(null)
        if (result.status === 'GRANTED') {
          applyConfig(result.config)
          setMessage({ tone: 'ok', text: `Chaîne connectée${result.config.youtubeChannelTitle ? ` : ${result.config.youtubeChannelTitle}` : ''}` })
          await loadPreview()
        } else if (result.status === 'DENIED') {
          setMessage({ tone: 'error', text: 'Autorisation refusée sur la page Google.' })
        } else {
          setMessage({ tone: 'error', text: 'Code expiré : relancez la connexion.' })
        }
      } catch (error) {
        setDevice(null)
        fail(error)
      }
    }, intervalSec * 1000)
  }, [stopPolling, applyConfig, loadPreview, fail])

  const handleAuthorize = useCallback(async () => {
    setBusy('authorize'); setMessage(null)
    try {
      const code = await youtubeApi.authorize()
      setDevice(code)
      poll(code.intervalSec)
    } catch (error) { fail(error) } finally { setBusy(null) }
  }, [poll, fail])

  const handleCancelAuthorize = useCallback(() => {
    stopPolling()
    setDevice(null)
  }, [stopPolling])

  const handleDisconnect = useCallback(async () => {
    if (!window.confirm('Déconnecter la chaîne YouTube ? Le titre du direct ne sera plus mis à jour.')) return
    setBusy('disconnect'); setMessage(null)
    try {
      const { config: saved } = await youtubeApi.disconnect()
      applyConfig(saved)
    } catch (error) { fail(error) } finally { setBusy(null) }
  }, [applyConfig, fail])

  const handleSync = useCallback(async () => {
    setBusy('sync'); setMessage(null)
    try {
      const outcome = await youtubeApi.sync()
      if (outcome.status === 'UPDATED') setMessage({ tone: 'ok', text: `Envoyé : « ${outcome.title} »` })
      else if (outcome.status === 'FAILED') setMessage({ tone: 'error', text: outcome.error })
      else if (outcome.status === 'SKIPPED' && outcome.reason === 'INCOMPLETE') {
        setMessage({ tone: 'error', text: 'Configuration incomplète : vidéo, identifiants et connexion de la chaîne.' })
      } else setMessage({ tone: 'ok', text: 'Rien à envoyer' })
      const [{ config: loaded }] = await Promise.all([youtubeApi.get(), loadPreview()])
      applyConfig(loaded)
    } catch (error) { fail(error) } finally { setBusy(null) }
  }, [applyConfig, loadPreview, fail])

  const handleReloadFooter = useCallback(async () => {
    setBusy('footer'); setMessage(null)
    try {
      const { description } = await youtubeApi.currentDescription()
      patch({ descriptionFooter: description })
      setMessage({ tone: 'ok', text: 'Description actuelle reprise : retirez-en la programmation éventuelle, puis enregistrez.' })
    } catch (error) { fail(error) } finally { setBusy(null) }
  }, [patch, fail])

  if (!config) {
    return <div className="settings-content--wide"><p><Spinner /> Chargement de la configuration…</p></div>
  }

  const clientIncomplete = !config.oauthClientId || (!config.hasOauthClientSecret && secret === '')
  const ready = config.connected && Boolean(config.videoId)

  return (
    <div className="settings-content--wide settings-youtube">
      <div className="settings-view-header">
        <div>
          <h2 className="settings-view-title">Direct YouTube</h2>
          <p className="settings-view-description">
            Titre et description du direct permanent, tenus à jour depuis la grille : le programme à l’antenne dans
            le titre, la programmation restante du jour dans la description.
          </p>
        </div>
        <div className="settings-view-header__actions">
          {message && (
            <span className={message.tone === 'ok' ? 'settings-saved' : 'settings-error'} role="status">
              {message.tone === 'ok' ? '✓ ' : '⚠ '}{message.text}
            </span>
          )}
          <button className="tp-button tp-button--primary" disabled={busy !== null || templateError !== null} onClick={() => void handleSave()}>
            {busy === 'save' ? 'Enregistrement…' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* ── Publication ─────────────────────────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="youtube-publish-title">
        <div className="settings-section__heading">
          <h3 id="youtube-publish-title" className="settings-section__title">Publication</h3>
          <p className="settings-section__description">
            Le titre change quand un programme prend l’antenne. Une coupure pub ou un jingle garde le titre du
            programme ; un infoservice, un interlude ou une antenne vide affichent le titre hors programme.
          </p>
        </div>

        <label className="settings-option-row settings-option-row--selectable">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={event => patch({ enabled: event.target.checked })}
          />
          <span className="settings-option-row__copy">
            <span className="settings-option-row__title">Mettre à jour le direct automatiquement</span>
            <span className="settings-option-row__description">
              Coupé, rien ne part vers YouTube ; l’aperçu et l’envoi manuel restent disponibles.
            </span>
          </span>
        </label>

        <div className="settings-redundancy__form-grid settings-youtube__grid">
          <label className="settings-youtube__wide">
            <span>Vidéo du direct (lien ou identifiant)</span>
            <input
              className="tp-input"
              placeholder="https://www.youtube.com/live/…"
              value={videoInput}
              onChange={event => { setVideoInput(event.target.value); setDirty(true) }}
              spellCheck={false}
            />
          </label>
          <div className="settings-youtube__wide settings-youtube__template">
            <label htmlFor="youtube-title-template">Modèle du titre</label>
            <input
              id="youtube-title-template"
              ref={templateInput}
              className="tp-input"
              value={config.titleTemplate}
              onChange={event => patch({ titleTemplate: event.target.value })}
              aria-invalid={templateError !== null}
              aria-describedby="youtube-title-template-help"
              spellCheck={false}
            />
            <div className="settings-youtube__markers" role="group" aria-label="Insérer un marqueur">
              {YOUTUBE_TITLE_MARKERS.map(marker => (
                <button
                  key={marker.token}
                  type="button"
                  className="tp-button"
                  onClick={() => insertMarker(marker.token)}
                  title={`Insérer ${marker.token} au curseur`}
                >
                  {marker.label}
                </button>
              ))}
            </div>
            <p
              id="youtube-title-template-help"
              className={templateError ? 'settings-youtube__template-error' : 'settings-section__description'}
              role={templateError ? 'alert' : undefined}
            >
              {templateError
                ?? '{sous-titre} ajoute lui-même « — » devant le sous-titre, et rien quand il n’y en a pas. Le numéro de partie n’apparaît jamais dans le titre.'}
            </p>
          </div>
          <label className="settings-youtube__wide">
            <span>Titre hors programme (infoservice, interlude, antenne vide)</span>
            <input
              className="tp-input"
              value={config.idleTitle}
              onChange={event => patch({ idleTitle: event.target.value })}
            />
          </label>
          <label>
            <span>Avance du titre (s)</span>
            <input
              className="tp-input" type="number" min={0} max={30}
              value={config.titleLeadSec}
              onChange={event => patch({ titleLeadSec: Math.min(30, Math.max(0, Math.round(Number(event.target.value) || 0))) })}
            />
          </label>
        </div>
        <p className="settings-section__description">
          YouTube affiche un nouveau titre 5 à 9 s après l’envoi : l’avance le fait partir ce nombre de secondes
          avant l’heure de grille du programme. À 0, le titre suit la prise d’antenne réelle.
        </p>

        <label className="settings-youtube__footer">
          <span>Fin de description (canaux, réseaux, hashtags)</span>
          <textarea
            className="tp-input"
            rows={10}
            value={config.descriptionFooter}
            onChange={event => patch({ descriptionFooter: event.target.value })}
            spellCheck={false}
          />
        </label>
        <div className="settings-actions">
          <button
            className="tp-button"
            disabled={busy !== null || !config.connected || !config.videoId || dirty}
            onClick={() => void handleReloadFooter()}
          >
            {busy === 'footer' ? 'Lecture…' : 'Reprendre la description actuelle du direct'}
          </button>
        </div>
      </section>

      {/* ── Aperçu ──────────────────────────────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="youtube-preview-title">
        <div className="settings-section__heading">
          <h3 id="youtube-preview-title" className="settings-section__title">Ce qui est publié maintenant</h3>
          <p className="settings-section__description">
            Calculé depuis la grille et l’antenne à cet instant, avec les réglages enregistrés. L’aperçu n’envoie rien.
          </p>
        </div>

        {preview
          ? (
            <div className="settings-report-preview">
              <div className="settings-report-preview__bar">
                <strong>{preview.title}</strong>
                <span>{preview.upToDate ? '✓ Déjà en ligne' : '● À envoyer'}</span>
              </div>
              <pre className="settings-report-preview__body">{preview.description}</pre>
            </div>
          )
          : <p className="settings-section__description">Aucun aperçu — vérifiez qu’une chaîne est active.</p>}

        <dl className="settings-youtube__status">
          <div><dt>Dernier envoi</dt><dd>{formatStamp(config.lastSyncedAt)}</dd></div>
          <div><dt>Titre envoyé</dt><dd>{config.lastTitle ?? '—'}</dd></div>
          {config.pauseReason && (
            <div>
              <dt>En pause</dt>
              <dd>
                {PAUSE_LABELS[config.pauseReason]}
                {config.pausedUntil ? ` jusqu’au ${formatStamp(config.pausedUntil)}` : ''}
              </dd>
            </div>
          )}
          {config.lastError && (
            <div className="is-failed">
              <dt>Dernière erreur</dt>
              <dd>⚠ {config.lastError} ({formatStamp(config.lastErrorAt)})</dd>
            </div>
          )}
        </dl>

        <div className="settings-actions">
          <button className="tp-button" disabled={busy !== null || dirty} onClick={() => void loadPreview()}>
            Actualiser l’aperçu
          </button>
          <button className="tp-button" disabled={busy !== null || dirty || !ready} onClick={() => void handleSync()}>
            {busy === 'sync' ? 'Envoi…' : 'Envoyer maintenant'}
          </button>
        </div>
        {dirty && (
          <p className="settings-notice settings-notice--warning">
            Enregistrez d’abord : l’aperçu et l’envoi utilisent les réglages enregistrés.
          </p>
        )}
      </section>

      {/* ── Connexion Google ────────────────────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="youtube-oauth-title">
        <div className="settings-section__heading">
          <h3 id="youtube-oauth-title" className="settings-section__title">Connexion à la chaîne</h3>
          <p className="settings-section__description">
            Identifiants OAuth d’un projet Google Cloud (API « YouTube Data API v3 », client de type « TV et
            appareils à saisie limitée », écran de consentement publié en Production). La connexion se fait avec
            le compte qui gère la chaîne.
          </p>
        </div>

        <div className="settings-redundancy__form-grid">
          <label className="settings-youtube__wide">
            <span>ID client OAuth</span>
            <input
              className="tp-input"
              placeholder="….apps.googleusercontent.com"
              value={config.oauthClientId ?? ''}
              onChange={event => patch({ oauthClientId: event.target.value || null })}
              spellCheck={false}
            />
          </label>
          <label>
            <span>Code secret du client</span>
            <input
              className="tp-input" type="password"
              placeholder={config.hasOauthClientSecret ? '•••••••• (enregistré)' : 'Non renseigné'}
              value={secret}
              onChange={event => { setSecret(event.target.value); setDirty(true) }}
              autoComplete="off"
            />
          </label>
        </div>
        <p className="settings-section__description">
          Le secret et l’autorisation sont chiffrés en base et jamais renvoyés par l’interface. Changer d’identifiants
          déconnecte la chaîne.
        </p>

        <p className="settings-notice">
          {config.connected
            ? `✓ Chaîne connectée${config.youtubeChannelTitle ? ` : ${config.youtubeChannelTitle}` : ''}`
            : 'Aucune chaîne connectée.'}
        </p>

        {device && (
          <div className="settings-report-preview" role="status">
            <div className="settings-report-preview__bar">
              <span>
                Ouvrez <a href={device.verificationUrl} target="_blank" rel="noreferrer">{device.verificationUrl}</a> et
                saisissez le code :
              </span>
              <button className="tp-button" onClick={handleCancelAuthorize}>Annuler</button>
            </div>
            <p className="settings-youtube__device-code">{device.userCode}</p>
          </div>
        )}

        <div className="settings-actions">
          <button
            className="tp-button"
            disabled={busy !== null || dirty || clientIncomplete || device !== null}
            onClick={() => void handleAuthorize()}
          >
            {busy === 'authorize' ? 'Demande…' : config.connected ? 'Reconnecter la chaîne' : 'Connecter la chaîne'}
          </button>
          {config.connected && (
            <button className="tp-button" disabled={busy !== null} onClick={() => void handleDisconnect()}>
              Déconnecter
            </button>
          )}
        </div>
      </section>
    </div>
  )
})
